import { Column } from '@orion-platform/datagrid';
import {
  EnumPartNumbersFieldStructure,
  PartNumbersListField,
} from '../models/partnumbers-list.model';
import {
  PART_NUMBERS_COLUMN_LIST,
  PART_NUMBERS_FIELDS_KEY,
} from '../pages/partnumber-list/partnumber-list.consts';
import { ColumnsState } from '../types/columns.types';
import { ColumnsService } from './columns.service';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from '@orion-platform/core';
import { environment } from '@orion/environment';
import { ApiQueryParams, QueryPayload } from 'apps/orion/src/app/models/app-config.model';
import { Observable } from 'rxjs';

import { BuyersContacts, DeleteBuyersContacts } from '../models/partnumbers-list.model';
import { FormSetting } from '../store/carriers-list/carriers-list.types';
import {
  BuyerContactGroupDto,
  DataGridResult,
  PartNumberAdditionInfoDto,
  PartNumberDto,
  PartNumberVendorCodeSectionNameDto,
  UpdateContactPayload,
  UpdatePartNumberAdditionInfoPayload,
  VendorSectionNameDto,
} from '../store/partnumbers-list/partnumbers-list.types';
import { PortfolioContactDto } from '../store/portfolio/portfolio.types';
import { PNVCSectionNamePayload } from '../store/vendors-list/vendors-list.types';
import { formatParams, generateHttpParams, getUrl } from '../utils/general.utils';
import { DefaultFormSettingsService } from './default-form-settings.service';

@Injectable({
  providedIn: 'root',
})
export class PartNumbersListService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly defaultForms = inject(DefaultFormSettingsService);
  private readonly columns = inject(ColumnsService);

  /**
   * The plant's allowed fields, merged with whatever the user saved.
   * @returns {Observable<ColumnsState>} The columns, and whether they are the defaults.
   */
  getDefaultColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new PartNumbersListField(EnumPartNumbersFieldStructure.PartNumberTable, formFields)
          .fields as Column[],
      columnListKey: PART_NUMBERS_COLUMN_LIST,
      formFields$: this.getDefaultFormSettingFields(PART_NUMBERS_FIELDS_KEY),
    });

  private readonly mm = environment.modulesBaseUrl.materialManagement;
  private readonly baseUrl = this.config.getSettings('apiBaseUrl') as string;

  // -----------------------
  // Repeated base endpoints
  // -----------------------
  private readonly partNumbersUrl = getUrl(this.baseUrl, this.mm.partNumbers);
  private readonly setPNVCSectionNameUrl = getUrl(
    this.baseUrl,
    this.mm.setPartNumberVendorCodeSectionName,
  );

  // -----------------------
  // PartNumbers
  // -----------------------
  getPartNumbersList(data: ApiQueryParams): Observable<DataGridResult<PartNumberDto>> {
    const params = formatParams(data);
    return this.http.get<DataGridResult<PartNumberDto>>(this.partNumbersUrl, { params });
  }

  // -----------------------
  // Contacts lists
  // -----------------------
  // -----------------------
  // Section name endpoints
  // -----------------------
  getVendorSectionName(partnumber: string): Observable<VendorSectionNameDto[]> {
    const url = getUrl(this.baseUrl, this.mm.vendorSectionName);
    const params = generateHttpParams({ partnumber });
    return this.http.get<VendorSectionNameDto[]>(url, { params });
  }

  setPartNumberCodeSectionName(
    payload: PNVCSectionNamePayload,
  ): Observable<PartNumberVendorCodeSectionNameDto> {
    return this.http.post<PartNumberVendorCodeSectionNameDto>(this.setPNVCSectionNameUrl, payload);
  }

  deletePartNumberCodeSectionName(data: {
    partNumber: string;
    vendorCode: string;
  }): Observable<PartNumberVendorCodeSectionNameDto> {
    return this.http.delete<PartNumberVendorCodeSectionNameDto>(
      `${this.partNumbersUrl}/DeletePartNumberVendorCodeSectionName/${data.partNumber}/${data.vendorCode}`,
    );
  }

  // -----------------------
  // Buyers Contacts
  // -----------------------
  createBuyersContacts(buyer: BuyersContacts): Observable<PortfolioContactDto> {
    return this.http.post<PortfolioContactDto>(`${this.partNumbersUrl}/createcontact`, buyer);
  }

  deleteBuyersContacts(buyer: DeleteBuyersContacts): Observable<boolean> {
    return this.http.post<boolean>(`${this.partNumbersUrl}/deletecontact`, buyer);
  }

  getBuyersContacts(data: QueryPayload): Observable<BuyerContactGroupDto[]> {
    const params = generateHttpParams({ $filter: (data.$filter ?? '') as string });
    return this.http.get<BuyerContactGroupDto[]>(`${this.partNumbersUrl}/getgroupcontacts`, {
      params,
    });
  }

  updateContact(contactData: UpdateContactPayload): Observable<PortfolioContactDto> {
    return this.http.post<PortfolioContactDto>(`${this.partNumbersUrl}/UpdateContact`, contactData);
  }

  // -----------------------
  // Additions
  // -----------------------
  getAdditionInfoByPartNumber(partNumber: string): Observable<PartNumberAdditionInfoDto[]> {
    const url = getUrl(this.baseUrl, this.mm.getAdditionInfoByPartNumber);
    const params = generateHttpParams({ partNumber });
    return this.http.get<PartNumberAdditionInfoDto[]>(url, { params });
  }

  createOrUpdateAdditionInfoByPartNumber(
    payload: UpdatePartNumberAdditionInfoPayload,
  ): Observable<PartNumberAdditionInfoDto> {
    const url = getUrl(this.baseUrl, this.mm.updatePartNumberAdditionInfo);
    return this.http.post<PartNumberAdditionInfoDto>(url, payload);
  }

  deletePartNumberAdditionInfo(data: {
    partNumber: string;
    propertyName: string;
  }): Observable<boolean> {
    return this.http.delete<boolean>(
      `${this.partNumbersUrl}/DeletePartNumberAdditionInfo/${data.partNumber}/${data.propertyName}`,
    );
  }

  // -----------------------
  // Default form settings
  // -----------------------
  getDefaultFormSettingFields(formKey: string): Observable<string[]> {
    return this.defaultForms.getFields(this.partNumbersUrl, formKey);
  }

  getDefaultFormSettingFormKeys(): Observable<string[]> {
    return this.defaultForms.formKeys(this.partNumbersUrl);
  }

  getDefaultFormSettings(formKey: string): Observable<FormSetting[]> {
    return this.defaultForms.getFormSettings(this.partNumbersUrl, formKey);
  }

  updateDefaultFormSettings(formKey: string, payload: FormSetting[]): Observable<FormSetting[]> {
    return this.defaultForms.update(this.partNumbersUrl, formKey, payload);
  }
}
